import axios from '../axiosInstance';
import qs from 'qs';

/**
 * DataGrid-style query for enabled locations (keyset pagination, sort, filter).
 * Backend: POST api/Locations/queryEnabled
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledLocations({
    search,
    pageSize,
    cursor,
    sortBy,
    sortDir,
    filterModel,
    locationTypes = ['STORE'],
    signal = null,
} = {}) {
    const body = {
        search: search ?? null,
        pageSize,
        cursor: cursor ?? null,
        sortBy: sortBy ?? 'name',
        sortDir: sortDir ?? 'asc',
        filterModel: filterModel ?? { items: [], logicOperator: 'and' },
        locationTypes: Array.isArray(locationTypes) && locationTypes.length
            ? locationTypes
            : ['STORE'],
    };

    const { data } = await axios.post('/Locations/queryEnabled', body, { signal });
    return data;
}

// GET enabled, paginated (kept for route loader / lookups)
export async function getLocationsEnabled({
    search = '',
    pageSize = 50,
    lastId = null,
    locationTypes = null,
    shipId = null,
    signal = null,
} = {}) {
    const params = new URLSearchParams();
    if (search != null) params.set('search', search);
    if (pageSize != null) params.set('pageSize', String(pageSize));
    if (lastId != null) params.set('lastId', String(lastId));
    if (Array.isArray(locationTypes) && locationTypes.length) {
        locationTypes.forEach(t => params.append('locationTypes', String(t)));
    }
    if (shipId != null && shipId !== '') {
        params.set('shipId', String(shipId));
    }
    const { data } = await axios.get('/Locations/getEnabled', { params, signal });
    return data; // { items, lastId, hasMore }
}

// GET by id
export async function getLocationById(id, signal = null) {
    const { data } = await axios.get(`/Locations/${id}`, { signal });
    return data;
}

// POST create
export async function createLocation(payload, signal = null) {
    const { data } = await axios.post('/Locations', payload, { signal });
    return data;
}

// PUT update
export async function updateLocation(id, payload, signal = null) {
    await axios.put(`/Locations/${id}`, payload, { signal });
    return true;
}

export async function deleteLocation(id, signal = null) {
    const { data } = await axios.delete(`/Locations/${id}`, { signal });
    return data;
}

export async function restoreLocation(id, signal = null) {
    await axios.post(`/Locations/${id}/restore`, null, { signal });
    return true;
}

/**
 * Fetch enabled locations by their IDs (for PPD filtering).
 * @param {number[]} ids - Array of location IDs to fetch
 * @returns {Promise<{items: Array}>} Promise resolving to locations data
 */
export async function getEnabledLocationsByIds(ids, signal = null) {
    if (!ids || ids.length === 0) return { items: [] };
    const res = await axios.post(
        "/Locations/byIdsEnabled",
        { filterModel: { items: [], logicOperator: 'and' } },
        {
            params: { ids },
            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
            signal,
        }
    );
    return res.data;
}

/**
 * Fetch lightweight location data by IDs (only id + display fields for weighing console).
 * Returns minimal data: id, name, erpCode for display purposes.
 * @param {number[]} ids - Array of location IDs to fetch
 * @returns {Promise<Array>} Promise resolving to array of lightweight location objects
 */
export async function getEnabledLocationsByIdsLightweight(ids, signal = null) {
    if (!ids || ids.length === 0) return [];
    const res = await axios.post(
        "/Locations/byIdsWeighingConsole",
        ids,
        { signal }
    );
    return res.data; // Returns array directly: [{ id, name, erpCode }]
}
